import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import {
  cleanHeadingText,
  createHeadingId,
  getHeadingText,
} from '../utils/articleHeadings';
import { markdownUrlTransform } from '../utils/markdownUrl';
import IconButton from './ui/IconButton';
import ArticleVideo from './ArticleVideo';
import StepCarousel from './StepCarousel';
import ImageCompare from './ImageCompare';
import PluginBlockView from '../plugins/PluginBlockView';
import usePlugins from '../plugins/usePlugins';
import { splitPluginBlocks } from '../plugins/pluginBlocks';
import ArticleTabs from './ArticleTabs';
import { splitStepGroups } from '../utils/stepGroups';
import { splitCompareBlocks } from '../utils/compareBlocks';
import { splitTabBlocks } from '../utils/tabBlocks';
import { splitMediaTextBlocks } from '../utils/mediaTextBlocks';
import rehypeSafeHtml from '../utils/safeHtml';
import rehypeEmojiIcons from '../utils/emojiIcons';

function createMarkdownHeadingRenderer(
  level,
  idQueue,
) {
  return function MarkdownHeadingRenderer({
    children,
  }) {
    const text = cleanHeadingText(
      getHeadingText(children),
    );

    const expectedBaseId =
      createHeadingId(text);

    const headingIndex = idQueue.findIndex(
      (heading) =>
        heading.level === level &&
        (
          heading.id === expectedBaseId ||
          heading.id.startsWith(
            `${expectedBaseId}-`,
          )
        ),
    );

    const heading =
      headingIndex >= 0
        ? idQueue.splice(
            headingIndex,
            1,
          )[0]
        : { id: expectedBaseId };

    const HeadingTag = `h${level}`;

    return (
      <HeadingTag
        id={heading.id}
        className="markdown-heading"
      >
        {children}
      </HeadingTag>
    );
  };
}

function openVideoInNewTab(src) {
  if (!src) {
    return
  }
  const link = document.createElement('a');
    link.href = src;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    document.body.appendChild(link);
    link.click();
    link.remove();

  }

function isVideoUrl(url = '') {
  if (!url) {
    return false;
  }

  const lowerUrl = url.toLowerCase();

  return (
    /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/.test(lowerUrl) ||
    lowerUrl.includes('/media/video/')
  );
}

function isAudioUrl(url = '') {
  if (!url) {
    return false;
  }

  return /\.(mp3|wav|ogg|oga|m4a|aac|flac)(\?.*)?$/i.test(url);
}

function VideoOpenButton({src}) {
  if (!src) {
    return null;
  }
  return (
    <button
      type="button"
      className="article-video-open"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        openVideoInNewTab(src);
      }}
      aria-label="Video im neuen Fenster öffnen"
      title="Video im neuen Fenster öffnen"
    >
      <i className="fa-solid fa-expand" />
    </button>
  );
}

function ArticleNavigation({
  navigation,
}) {
  const { t } = useTranslation();
  const previous = navigation?.previous;
  const next = navigation?.next;

  if (!previous && !next) {
    return null;
  }

  return (
    <nav
      className="article-navigation"
      aria-label={t('articlePage.navigationAriaLabel')}
    >
      <div className="article-navigation-item article-navigation-previous">
        {previous && (
          <Link
            to={`/article/${previous.article_id}`}
          >
            <span className="article-navigation-direction">
              {t('articlePage.previousLabel')}
            </span>

            <span className="article-navigation-title">
              {previous.title ||
                t('articlePage.articleFallback', { id: previous.article_id })}
            </span>
          </Link>
        )}
      </div>

      <div className="article-navigation-item article-navigation-next">
        {next && (
          <Link
            to={`/article/${next.article_id}`}
          >
            <span className="article-navigation-direction">
              {t('articlePage.nextLabel')}
            </span>

            <span className="article-navigation-title">
              {next.title ||
                t('articlePage.articleFallback', { id: next.article_id })}
            </span>
          </Link>
        )}
      </div>
    </nav>
  );
}

function ArticleBody({
  articleText,
  headings,
  navigation,
  departmentId = null,
}) {
  const { t } = useTranslation();
  const plugins = usePlugins();

  /*
   * Memoized so scroll-driven activeHeadingId updates in the parent
   * (many times a second while scrolling) don't force React to recreate
   * the ReactMarkdown `components` map on every render. A fresh map
   * means new component identities for img/video/audio, which React
   * then treats as unrelated element types - unmounting and remounting
   * every image and video in the article on every scroll tick. Only
   * recompute when the article content itself actually changes.
   */
  return useMemo(() => {
    /*
     * A separate queue is needed for every
     * Markdown render so identical headings
     * receive the same IDs as the table
     * of contents entries.
     */
    const headingIdQueue =
      headings.map((heading) => ({
        ...heading,
      }));

    const components = {
            h2:
              createMarkdownHeadingRenderer(
                2,
                headingIdQueue,
              ),

            h3:
              createMarkdownHeadingRenderer(
                3,
                headingIdQueue,
              ),

            h4:
              createMarkdownHeadingRenderer(
                4,
                headingIdQueue,
              ),

            a({
              href,
              children,
              ...props
            }) {
              const isExternal =
                href?.startsWith('http://') ||
                href?.startsWith('https://');

              const isInternalArticle = href?.startsWith('/article/');

              const isVideo = isVideoUrl(href);
              const isAudio = isAudioUrl(href);

              if (isVideo) {
                return (
                  <ArticleVideo
                    src={href}
                    openButton={<VideoOpenButton src={href} />}
                  />
                );
              }

               if (isAudio) {
                return (
                  <span className="article-audio-wrapper">
                  <audio
                    src={href}
                    controls
                    controlsList="nofullscreen"
                    preload="metadata"
                    className="article-audio"
                  >
                    {children}
                  </audio>

                  <IconButton
                    className="article-audio-subtitles-button"
                    icon="fa-solid fa-file-lines"
                    label={t('articlePage.transcription')}
                    onClick={() => {
                      alert(t('articlePage.transcriptionUnavailable'));
                    }}
                  >
                    <span className="article-audio-subtitles-tooltip">
                      {t('articlePage.transcription')}
                    </span>
                  </IconButton>
                </span>
                );
              }

              // A click simulation opens beside the article, which stays where
              // the reader was (pages/SimulationLaunch.js).
              if (href?.startsWith('/simulation/')) {
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="article-simulation-link"
                    {...props}
                  >
                    <i className="fa-solid fa-computer-mouse" aria-hidden="true" />
                    {children}
                  </a>
                );
              }

              if (isInternalArticle) {
                return (
                  <Link
                    to={href}
                    {...props}
                  >
                    {children}
                  </Link>
                );
              }

              return (
                <a
                  href={href}
                  target={ isExternal
                      ? '_blank'
                      : undefined
                  }
                  rel={ isExternal
                      ? 'noopener noreferrer'
                      : undefined
                  }
                  {...props}
                >
                  {children}
                </a>
              );
            },

            img({
              src,
              alt,
              className,
              ...props
            }) {
              if (!src){
                return null;
              }

              const image = (
                <img
                  {...props}
                  src={src}
                  alt={alt ?? ''}
                  loading="lazy"
                  decoding="async"
                  className={className || 'article-image'}
                />
              );

              // An icon in an imported PDF table is part of the table's text,
              // not a picture to open on its own.
              if (className === 'pdf-import-icon') {
                return image;
              }

              const link = (
                <a
                  href = {src}
                  target ='_blank'
                  rel = 'noopener noreferrer'
                  className = 'article-image-link'
                >
                  {image}
                </a>
              );

              // ![alt](src "Caption"): the title is shown under the picture.
              // Spans, because the picture sits inside a paragraph.
              if (!props.title) return link;

              return (
                <span className="article-figure">
                  {link}
                  <span className="article-figure-caption">{props.title}</span>
                </span>
              );
            },

            video({
              src,
              ...props
            }) {
              if (!src){
                return null;
            }

             return (
               <span className="article-video-wrapper">

                <video
                  src={src}
                  controls
                  preload="metadata"
                  className="article-video"
                  {...props}
                />

                <VideoOpenButton src={src}/>
              </span>
          );
        },
      };

    const renderPlain = (text, key) => (
      <ReactMarkdown
        key={key}
        urlTransform={markdownUrlTransform}
        remarkPlugins={[
          remarkGfm,
        ]}
        // HTML in the text (tables and coloured blocks from the PDF import,
        // or typed by an author) is rendered - but only what safeHtml allows.
        rehypePlugins={[
          rehypeRaw,
          rehypeSafeHtml,
          // after the filter: the icons it adds are the only classes let in
          rehypeEmojiIcons,
        ]}
        components={components}
      >
        {text}
      </ReactMarkdown>
    );

    // ":::vergleich" blocks become before/after sliders, in the text and
    // inside steps alike (utils/compareBlocks.js).
    const renderCompare = (text, key) => (
      <Fragment key={key}>
        {splitCompareBlocks(text).map((segment, index) => (
          segment.type === 'compare'
            ? (
              <ImageCompare
                key={`${key}-compare-${index}`}
                images={segment.images}
                srcFor={markdownUrlTransform}
              />
            )
            : renderPlain(segment.text, `${key}-${index}`)
        ))}
      </Fragment>
    );

    // Blocks of plugins switched on somewhere (plugins/pluginBlocks.js) are
    // drawn by the plugin; while the list loads, they wait as text.
    const renderPluginBlocks = (text, key) => (
      <Fragment key={key}>
        {splitPluginBlocks(text, plugins ?? []).map((segment, index) => (
          segment.type === 'plugin'
            ? (
              <PluginBlockView
                key={`${key}-plugin-${index}`}
                plugin={segment.plugin}
                data={segment.data}
                departmentId={departmentId}
              />
            )
            : renderCompare(segment.text, `${key}-${index}`)
        ))}
      </Fragment>
    );

    // Plugin blocks first; what is left may hold image comparisons. A
    // ":::bild-text" block puts a picture beside or above its text
    // (utils/mediaTextBlocks.js), and its text is drawn the same way.
    const renderMarkdown = (text, key = 'md') => (
      <Fragment key={key}>
        {splitMediaTextBlocks(text).map((segment, index) => {
          if (segment.type !== 'mediaText') return renderPluginBlocks(segment.text, `${key}-${index}`);

          const src = segment.image && markdownUrlTransform(segment.image.src);

          return (
            <div key={`${key}-media-text-${index}`} className={`article-media-text is-${segment.layout}`}>
              {src && (
                <a
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="article-media-text-image article-image-link"
                >
                  <img
                    src={src}
                    alt={segment.image.alt}
                    loading="lazy"
                    decoding="async"
                    className="article-image"
                  />
                </a>
              )}
              <div className="article-media-text-body">
                {renderPluginBlocks(segment.text, `${key}-${index}-text`)}
              </div>
            </div>
          );
        })}
      </Fragment>
    );

    // Runs of "Schritt n" sections are shown as a carousel; everything
    // else renders as before (utils/stepGroups.js).
    const renderSections = (text, key) => (
      <Fragment key={key}>
        {splitStepGroups(text).map((segment, index) => (
          segment.type === 'steps'
            ? (
              <StepCarousel
                key={`${key}-steps-${index}`}
                steps={segment.steps}
                renderMarkdown={(stepText) => renderMarkdown(stepText)}
              />
            )
            : renderMarkdown(segment.text, `${key}-text-${index}`)
        ))}
      </Fragment>
    );

    // ":::tabs" blocks become tabs, whose panels hold the same kinds of
    // content as the article itself (utils/tabBlocks.js).
    const segments = splitTabBlocks(articleText ?? '');

    return (
      <div className="maincol markdown-content">
        {segments.map((segment, index) => (
          segment.type === 'tabs'
            ? (
              <ArticleTabs
                key={`tabs-${index}`}
                intro={segment.intro}
                tabs={segment.tabs}
                renderMarkdown={renderSections}
              />
            )
            : renderSections(segment.text, `section-${index}`)
        ))}

        <ArticleNavigation
          navigation={
            navigation
          }
        />
      </div>
    );
  }, [articleText, headings, navigation, t, plugins, departmentId]);
}

export default ArticleBody;
